import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    PageHeader,
    PublicId,
    StatusBadge,
} from '@bemor/web-kit/ui';

import type { StaffUserRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/users';
const PAGE_KEY = 'users';

/** BM-93 (design 0.9.0 `backoffice/users`): every merchant user of the tenant; recovery and "Sign in as" from the page. */
export const userColumns: ListColumnDef<StaffUserRow>[] = [
    {
        key: 'name',
        label: 'User',
        sortKey: 'name',
        cell: (u) => (
            <span className="flex min-w-0 flex-col">
                <span className="font-medium">{u.name}</span>
                <span className="text-muted-foreground truncate text-xs">
                    {u.email}
                </span>
            </span>
        ),
        csv: (u) => u.email,
        locked: 'start',
    },
    {
        key: 'id',
        label: 'Id',
        cell: (u) => <PublicId id={u.id} />,
        csv: (u) => u.id,
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (u) => <StatusBadge status={u.status} domain="user" />,
        csv: (u) => u.status,
        groupable: { key: 'status', statusDomain: 'user' },
    },
    {
        key: 'totp_enabled',
        label: 'Two-factor',
        cell: (u) => (u.totp_enabled ? 'On' : 'Off'),
        csv: (u) => (u.totp_enabled ? 'on' : 'off'),
        noTruncate: true,
    },
    {
        key: 'last_login_at',
        label: 'Last sign-in',
        kind: 'datetime',
        sortKey: 'last_login_at',
        cell: (u) => <DateTime value={u.last_login_at} empty="never" />,
        csv: (u) => u.last_login_at,
        noTruncate: true,
    },
    {
        key: 'organizations_count',
        label: 'Organizations',
        cell: (u) => u.organizations_count,
        csv: (u) => u.organizations_count,
        noTruncate: true,
    },
    {
        key: 'created_at',
        aliases: ['created'],
        label: 'Since',
        kind: 'datetime',
        sortKey: 'created_at',
        cell: (u) => <DateTime value={u.created_at} format="date" empty="—" />,
        csv: (u) => u.created_at,
        noTruncate: true,
        defaultHidden: true,
    },
];

export const userFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    organization_id: 'Organization',
    merchant_id: 'Merchant',
    range: 'Created',
});

export const usersPrefetch = adminListPrefetch(PAGE_KEY, PATH);

export function UsersPage() {
    const { state, query } = useAdminList<StaffUserRow>(PAGE_KEY, PATH, {
        columns: userColumns,
        filters: userFilters,
    });
    return (
        <div>
            <PageHeader
                title="Users"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Every person with a merchant or organization account. Recovery and "Sign in as" from the detail page.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="users"
                state={state}
                query={query}
                rowKey={(u) => u.id}
                rowHref={(u) => `/users/${u.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'user' },
                    organization_id: { kind: 'text', label: 'Organization id' },
                    merchant_id: { kind: 'text', label: 'Merchant id' },
                }}
            />
        </div>
    );
}
